import { useEffect, useState } from 'react';
import { Copy, Download, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PaymentLink } from '@masonic-tools/shared-types';
import { copyTextToClipboard } from '@/lib/clipboard-utils';
import { showError, showSuccess } from '@/lib/swal';
import { downloadBlob, downloadDataUrl, slugify } from '@/lib/unit-payment-links';
import { buildPaymentLinkPoster, copyQrImageToClipboard, paymentLinkQrPng, paymentLinkQrSvg } from '@/lib/payment-link-qr';

/** PNG / SVG / poster downloads for a link. Shared by the QR dialog, success step and share panel. */
export function usePaymentLinkDownloads(link: PaymentLink | null, unitName: string) {
  const [busy, setBusy] = useState<'png' | 'svg' | 'poster' | null>(null);
  const slug = link ? slugify(link.reference || link.title) : 'payment-link';

  const run = async (kind: 'png' | 'svg' | 'poster', fn: () => Promise<void>) => {
    if (!link) return;
    setBusy(kind);
    try {
      await fn();
    } catch {
      showError('Could not create the download. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  return {
    busy,
    downloadPng: () => run('png', async () => downloadDataUrl(await paymentLinkQrPng(link!.shortUrl), `${slug}-qr.png`)),
    downloadSvg: () =>
      run('svg', async () => downloadBlob(new Blob([await paymentLinkQrSvg(link!.shortUrl)], { type: 'image/svg+xml' }), `${slug}-qr.svg`)),
    downloadPoster: () => run('poster', async () => downloadBlob(await buildPaymentLinkPoster(link!, unitName), `${slug}-poster.pdf`)),
  };
}

export function useQrPreview(url: string | undefined, width = 320): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!url) return;
    paymentLinkQrPng(url, width)
      .then((png) => !cancelled && setSrc(png))
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
    };
  }, [url, width]);
  return src;
}

export async function copyLink(url: string) {
  const ok = await copyTextToClipboard(url);
  if (ok) showSuccess('Link copied');
  else showError('Could not copy the link');
}

interface Props {
  link: PaymentLink | null;
  unitName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaymentLinkQrDialog({ link, unitName, open, onOpenChange }: Props) {
  const preview = useQrPreview(open ? link?.shortUrl : undefined, 640);
  const { busy, downloadPng, downloadSvg, downloadPoster } = usePaymentLinkDownloads(link, unitName);
  const canCopyImage = typeof window !== 'undefined' && 'ClipboardItem' in window;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[95vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>QR code</DialogTitle>
          <DialogDescription>Print it, add it to a summons or put it on the tables.</DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="mt-4 space-y-4">
            <div className="mx-auto flex aspect-square w-full max-w-[280px] items-center justify-center rounded-lg border border-border bg-white p-2">
              {preview ? <img src={preview} alt={`QR code for ${link.title}`} className="h-full w-full" /> : <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
            </div>
            <div className="text-center">
              <p className="font-semibold">{link.title}</p>
              <p className="break-all font-mono text-sm text-muted-foreground">{link.shortUrl}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={downloadPng} disabled={!!busy}>
                {busy === 'png' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!busy}>
                {busy === 'svg' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}SVG
              </Button>
              <Button variant="outline" onClick={downloadPoster} disabled={!!busy}>
                {busy === 'poster' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Poster (PDF)
              </Button>
              <Button variant="outline" onClick={() => copyLink(link.shortUrl)}>
                <Copy className="mr-2 h-4 w-4" />
                Copy link
              </Button>
              {canCopyImage && preview ? (
                <Button
                  variant="ghost"
                  className="col-span-2"
                  onClick={async () => ((await copyQrImageToClipboard(preview)) ? showSuccess('QR image copied') : showError('Could not copy the image'))}
                >
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Copy image
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
